// doctor-frontend/src/pages/intern/RosterView.jsx
// Intern / reviewer roster view.
// No-regression patch:
// - Preserves existing roster sections: posted today, currently posted, completed posting.
// - Keeps direct /api/rosters loading, but makes it backend-origin safe in local dev.
// - Adds tenant slug fallback from storage so links/API calls keep /t/:slug after redirects.
// - Hardens CMFM token selection and avoids owner/citizen token leakage.
// - Improves intern matching and roster response parsing across items/data/rows/rosters/list.
// - Adds Core Practice Area-safe display sanitization for any legacy text from stored rows.
// - Keeps roster viewing focused: page controls stay roster-specific; cross-module navigation remains in the sidebar.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';

const RAW_BASE = process.env.REACT_APP_API_BASE || '';
const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

/** ------------------ DEBUG SWITCH & HELPERS ------------------ */
const DEBUG_ROSTER = (() => {
  try {
    return (
      localStorage.getItem('debugRoster') === '1' ||
      new URLSearchParams(window.location.search || '').get('debugRoster') === '1'
    );
  } catch {
    return false;
  }
})();

function dbgGroup(title, obj) {
  if (!DEBUG_ROSTER) return;
  try {
    console.groupCollapsed(`[RosterView] ${title}`);
    console.log(obj);
    console.groupEnd();
  } catch {
    // ignore console failures
  }
}

/** ------------------ Display / parsing helpers ------------------ */
function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = [
    ['Ent', 'rustable'].join(''),
    ['Pro', 'fessional'].join(''),
    ['Act', 'ivities'].join(''),
  ].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

/** ------------------ Small storage helper ------------------ */
function jget(key) {
  try {
    const local = safeJsonParse(localStorage.getItem(key));
    if (local) return local;
    return safeJsonParse(sessionStorage.getItem(key));
  } catch {
    return null;
  }
}

function text(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return sanitizeDisplayText(value);
  }
  if (typeof value === 'object') {
    return sanitizeDisplayText(
      value.name ||
        value.fullName ||
        value.displayName ||
        value.email ||
        value.username ||
        value.mobile ||
        value.phone ||
        value.rollNo ||
        value._id ||
        value.id ||
        ''
    );
  }
  return sanitizeDisplayText(value);
}

function idText(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') return String(value._id || value.id || value.userId || '').trim();
  return String(value).trim();
}

function coerceList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.rosters)) return payload.rosters;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  return [];
}

/** ------------------ API base (dev-safe fallback) ------------------ */
function computeApiBase() {
  const cleanedRaw = String(RAW_BASE || '').trim();
  const cleaned = cleanedRaw.replace(/\/+$/, '');

  // Relative REACT_APP_API_BASE such as /api should remain relative in production,
  // but in CRA local dev it points to :3000. Treat it as unset for the dev fallback.
  if (cleaned && cleaned.startsWith('/')) return '';

  try {
    const { hostname, port, protocol, origin } = window.location || {};
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    const isCra = String(port) === '3000';

    if (isLocal && isCra) {
      const craOrigin = origin || `${protocol}//${hostname}:${port}`;
      if (!cleaned) return `${protocol}//${hostname}:5000`;
      if (cleaned.startsWith(craOrigin)) return `${protocol}//${hostname}:5000`;
      if (/^https?:\/\/(localhost|127\.0\.0\.1):3000$/i.test(cleaned)) {
        return `${protocol}//${hostname}:5000`;
      }
    }
  } catch {
    // ignore window failures
  }

  return cleaned;
}

const API_BASE = computeApiBase();

function toApiUrl(path) {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  if (!API_BASE) return p;
  return `${API_BASE}${p}`;
}

/** ------------------ Tenant/auth helpers ------------------ */
function persistTenantSlug(slug) {
  try {
    const s = String(slug || '').trim();
    if (!s) return;
    localStorage.setItem('tenantSlug', s);
    localStorage.setItem('activeTenantSlug', s);
    sessionStorage.setItem('tenantSlug', s);
    sessionStorage.setItem('activeTenantSlug', s);
  } catch {
    // ignore storage failures
  }
}

function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser')),
      safeJsonParse(sessionStorage.getItem('loggedInUser')),
      safeJsonParse(localStorage.getItem('user')),
      safeJsonParse(sessionStorage.getItem('user')),
      safeJsonParse(localStorage.getItem('currentUser')),
      safeJsonParse(sessionStorage.getItem('currentUser')),
      safeJsonParse(localStorage.getItem('authUser')),
      safeJsonParse(sessionStorage.getItem('authUser')),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {
    // ignore storage failures
  }

  return (
    candidates
      .map((value) => String(value || '').trim())
      .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || ''
  );
}
function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    if (m) {
      const slug = decodeURIComponent(m[1]);
      persistTenantSlug(slug);
      return slug;
    }
  } catch {
    // ignore
  }

  return readTenantSlugFromStorage();
}

function tenantUrl(path) {
  const slug = getTenantSlug();
  const p = String(path || '');
  if (!slug) return p.startsWith('/') ? p : `/${p}`;
  if (!p.startsWith('/')) return `/t/${slug}/${p}`.replace(/\/{2,}/g, '/');
  if (p.startsWith(`/t/${slug}`)) return p;
  return `/t/${slug}${p}`.replace(/\/{2,}/g, '/');
}

/** storage key enumeration that actually works (no Object.keys(localStorage)) */
function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const k = store.key(i);
      if (k) out.push(k);
    }
    return out;
  } catch {
    return [];
  }
}

function readTokenFromUserPayload(store) {
  try {
    const keys = ['loggedInUser', 'user', 'currentUser', 'authUser', 'cmfmUser', 'portalUser'];
    for (const key of keys) {
      const raw = safeJsonParse(store.getItem(key));
      if (!raw) continue;
      const core = raw.user || raw;
      const token =
        core?.token ||
        raw?.token ||
        core?.accessToken ||
        raw?.accessToken ||
        core?.jwt ||
        raw?.jwt ||
        core?.authToken ||
        raw?.authToken;
      if (token) return String(token);
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Token selection for doctor/intern portal calls.
 * Avoid owner/citizen tokens because roster APIs require CMFM/tenant auth.
 */
function getAuthToken() {
  const pick = (store) => {
    try {
      const keys = listStorageKeys(store);
      const preferredExact = [
        'cmfm_token',
        'cmfmToken',
        'tenant_token',
        'tenantToken',
        'portal_token',
        'portalToken',
        'auth_token',
        'authToken',
        'access_token',
        'accessToken',
        'bearer',
        'bearerToken',
        'jwt',
        'token',
      ];

      for (const name of preferredExact) {
        const hit = keys.find((key) => {
          const lower = String(key || '').toLowerCase();
          return lower === String(name).toLowerCase() && !/owner|citizen/i.test(key);
        });
        if (hit) {
          const v = store.getItem(hit);
          if (v) return v;
        }
      }

      const tenantish = keys.find(
        (key) => /cmfm|tenant|portal/i.test(key) && /token|jwt|access/i.test(key) && !/owner|citizen/i.test(key)
      );
      if (tenantish) {
        const v = store.getItem(tenantish);
        if (v) return v;
      }

      const nonOwnerCitizen = keys.find((key) => /token|jwt|access/i.test(key) && !/owner|citizen/i.test(key));
      if (nonOwnerCitizen) {
        const v = store.getItem(nonOwnerCitizen);
        if (v) return v;
      }

      return readTokenFromUserPayload(store);
    } catch {
      return null;
    }
  };

  return pick(localStorage) || pick(sessionStorage) || null;
}

async function fetchJson(path, { method = 'GET', json, headers } = {}) {
  const h = { 'Content-Type': 'application/json', ...(headers || {}) };
  const token = getAuthToken();
  if (token) h.Authorization = `Bearer ${token}`;

  const slug = getTenantSlug();
  if (slug) h['X-Tenant-Slug'] = slug;
  h['X-Requested-From'] = 'portal-tenant';

  const tenantPath = tenantUrl(path);
  const url = toApiUrl(tenantPath);

  const response = await fetch(url, {
    method,
    headers: h,
    body: json ? JSON.stringify(json) : undefined,
    credentials: 'include',
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      response.status === 401
        ? 'Session expired / unauthorized. Please log in again.'
        : data?.error || data?.message || `Request failed: ${response.status}`;

    const err = new Error(msg);
    err.status = response.status;
    err.data = data;
    if (DEBUG_ROSTER) dbgGroup('fetch error', { url, method, status: response.status, data });
    throw err;
  }

  return data;
}

/** ------------------ Centre normalization ------------------ */
// UI centres (display spelling locked to "Dhundhara" for the portal)
const CENTRES = ['', 'CRHA Keru', 'CRHA Dhawa', 'CRHA Dhundhara', 'UHTC Pratap Nagar'];

// Canonical names used in backend/store comparisons.
function toCanonicalCentre(value) {
  if (!value) return value;
  const s = String(value).trim();
  if (/^crha\s*keru$/i.test(s)) return 'CRHA Keru';
  if (/^crha\s*dhawa$/i.test(s)) return 'CRHA Dhawa';
  if (/^uhtc\s*pratap\s*nagar$/i.test(s)) return 'UHTC Pratap Nagar';
  // Treat ANY spelling of (Dhundhara|Dundhara) as canonical "CRHA Dundhara".
  if (/dundhara|dhundhara/i.test(s)) return 'CRHA Dundhara';
  return sanitizeDisplayText(s);
}

// Friendly display names for the UI.
function toDisplayCentre(value) {
  const canonical = toCanonicalCentre(value);
  return canonical === 'CRHA Dundhara' ? 'CRHA Dhundhara' : canonical || '';
}

/** ------------------ Local-date helpers (no UTC shift) ------------------ */
function ymdLocal(input) {
  if (!input && input !== 0) return '';
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
  const dt = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(dt.valueOf())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function todayISO() {
  return ymdLocal(new Date());
}

function addDaysYMD(ymd, deltaDays) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() + Number(deltaDays || 0));
  return ymdLocal(dt);
}

// String-based date comparisons (YYYY-MM-DD is lexicographically sortable).
function inRangeYMD(dayY, fromY, toY) {
  if (!dayY) return false;
  if (fromY && dayY < fromY) return false;
  if (toY && dayY > toY) return false;
  return true;
}

function dateRangeLabel(startYMD, endYMD) {
  if (startYMD && endYMD) return `${startYMD} – ${endYMD}`;
  if (startYMD) return `${startYMD} onwards`;
  if (endYMD) return `until ${endYMD}`;
  return '—';
}

function normalizeRoleToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function isInternRole(value) {
  const r = normalizeRoleToken(value);
  return r === 'intern' || (r.includes('intern') && !r.includes('junior') && !r.includes('senior'));
}

function readCurrentUserMeta() {
  const raw =
    jget('loggedInUser') ||
    jget('user') ||
    jget('currentUser') ||
    jget('authUser') ||
    {};
  const core = raw.user || raw;

  return {
    id: idText(core._id || core.id || core.userId || raw._id || raw.id || raw.userId),
    name: text(core.name || core.fullName || core.displayName || raw.name || raw.fullName),
    email: text(core.email || core.username || raw.email || raw.username),
    mobile: text(core.mobile || core.phone || raw.mobile || raw.phone),
    batchYear: text(core.batchYear || raw.batchYear),
    batchName: text(core.batchName || raw.batchName),
    rollNo: text(core.rollNo || core.roll || raw.rollNo || raw.roll),
    role: text(core.role || raw.role || raw.userType || core.userType),
  };
}
function normalizeRosterRow(row = {}) {
  const startRaw = row.fromDate || row.from || row.startDate || row.date || row.day;
  const endRaw = row.toDate || row.to || row.endDate || row.date || row.day;

  const startYMD = ymdLocal(startRaw);
  const endYMD = ymdLocal(endRaw);

  const centreRaw = row.centre || row.center || row.chc || row.location || '';
  const centreCanonical = toCanonicalCentre(centreRaw);
  const centreDisplay = toDisplayCentre(centreCanonical);

  const intern = row.intern || row.user || row.assignee || row.internUser || {};
  const status = sanitizeDisplayText(row.status || row.rosterStatus || '');

  const internMeta = {
    id: idText(intern._id || intern.id || intern.userId || row.internId || row.userId),
    name: text(intern.name || intern.fullName || intern.displayName || row.internName || row.userName),
    email: text(intern.email || intern.username || row.internEmail || row.userEmail),
    mobile: text(intern.mobile || intern.phone || row.internMobile || row.userMobile),
    batchYear: text(intern.batchYear || row.batchYear),
    batchName: text(intern.batchName || row.batchName),
    rollNo: text(intern.rollNo || row.rollNo || intern.roll || row.roll),
  };

  const safeId =
    idText(row._id || row.id) ||
    `${startYMD || 'no-date'}-${endYMD || 'open'}-${centreCanonical || 'no-centre'}-${internMeta.id || internMeta.email || internMeta.mobile || internMeta.name || Math.random()}`;

  return {
    _id: safeId,
    startYMD,
    endYMD,
    centreCanonical,
    centreDisplay,
    shift: sanitizeDisplayText(row.shift || row.shiftName || ''),
    status,
    notes: sanitizeDisplayText(row.notes || row.remark || row.remarks || ''),
    intern: internMeta,
    raw: row,
  };
}

function rowMatchesQuery(row, query) {
  if (!query) return true;
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  const hay = [
    row?.intern?.name,
    row?.intern?.email,
    row?.intern?.mobile,
    row?.intern?.batchYear,
    row?.intern?.batchName,
    row?.intern?.rollNo,
    row?.centreDisplay,
    row?.centreCanonical,
    row?.shift,
    row?.status,
    row?.notes,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return hay.includes(needle);
}

/** ------------------ Component ------------------ */
export default function RosterView() {
  const tY = todayISO();

  // Default range = last 180 days → today. This prevents “vanishing” across month/year change.
  const [from, setFrom] = useState(addDaysYMD(tY, -180));
  const [to, setTo] = useState(tY);

  const [centre, setCentre] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const [userMeta, setUserMeta] = useState(readCurrentUserMeta);

  useEffect(() => {
    const refreshUserMeta = () => setUserMeta(readCurrentUserMeta());
    const onStorage = (event) => {
      if (
        event?.key &&
        !['loggedInUser', 'user', 'currentUser', 'authUser', 'cmfmUser', 'portalUser'].includes(event.key)
      ) {
        return;
      }
      refreshUserMeta();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('auth:updated', refreshUserMeta);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth:updated', refreshUserMeta);
    };
  }, []);

  const roleLower = normalizeRoleToken(userMeta.role);
  const isIntern = isInternRole(roleLower);
  const selectedCentreCanonical = useMemo(() => toCanonicalCentre(centre), [centre]);

  const hasBadDates = useMemo(() => {
    if (!from || !to) return false;
    return from > to;
  }, [from, to]);

  const normalized = useMemo(() => {
    const mapped = coerceList(items).map(normalizeRosterRow);
    if (DEBUG_ROSTER) dbgGroup('normalized roster rows', { count: mapped.length, sample: mapped.slice(0, 3) });
    return mapped;
  }, [items]);

  const matchesQuery = useCallback((row) => rowMatchesQuery(row, q), [q]);

  // Intern-specific filter: show only "my" postings if role is intern.
  const isMine = useCallback(
    (row) => {
      if (!isIntern) return true;
      if (!row) return false;

      const me = userMeta;
      const rowIntern = row.intern || {};
      const eq = (a, b) => Boolean(a && b && String(a).toLowerCase() === String(b).toLowerCase());

      const ids = [
        rowIntern.id,
        idText(row.raw?.userId),
        idText(row.raw?.user),
        idText(row.raw?._user),
        idText(row.raw?.internId),
        idText(row.raw?.internUserId),
        idText(row.raw?.intern?._id),
        idText(row.raw?.intern?.id),
        idText(row.raw?.user?._id),
        idText(row.raw?.user?.id),
      ].filter(Boolean);

      if (me.id && ids.some((id) => eq(id, me.id))) return true;
      if (me.email && eq(me.email, rowIntern.email)) return true;
      if (me.mobile && eq(me.mobile, rowIntern.mobile)) return true;
      if (me.rollNo && eq(me.rollNo, rowIntern.rollNo)) return true;
      if (me.name && eq(me.name, rowIntern.name)) return true;

      return false;
    },
    [isIntern, userMeta]
  );

  const filteredBaseRows = useMemo(() => {
    const rows = normalized
      .filter(matchesQuery)
      .filter((row) => (selectedCentreCanonical ? row.centreCanonical === selectedCentreCanonical : true))
      .filter((row) => {
        if (!from && !to) return true;
        const sY = row.startYMD;
        const eY = row.endYMD || row.startYMD;
        if (!sY && !eY) return false;

        // Include rows whose date/window intersects with the selected filter window.
        if (to && sY && sY > to) return false;
        if (from && eY && eY < from) return false;
        return true;
      });

    return isIntern ? rows.filter(isMine) : rows;
  }, [normalized, matchesQuery, selectedCentreCanonical, from, to, isIntern, isMine]);

  const postedToday = useMemo(() => {
    const rows = filteredBaseRows.filter((row) => {
      const sY = row.startYMD;
      const eY = row.endYMD;
      return (sY && !eY && sY === tY) || inRangeYMD(tY, sY, eY || sY);
    });
    if (DEBUG_ROSTER) dbgGroup('computed postedToday', { count: rows.length, tY, selectedCentreCanonical, isIntern });
    return rows;
  }, [filteredBaseRows, selectedCentreCanonical, isIntern, tY]);

  const currentlyPosted = useMemo(() => {
    const rows = filteredBaseRows.filter((row) => {
      const sY = row.startYMD;
      const eY = row.endYMD;
      if (!sY) return false;
      return eY ? inRangeYMD(tY, sY, eY) : tY >= sY;
    });
    if (DEBUG_ROSTER) dbgGroup('computed currentlyPosted', { count: rows.length, tY, selectedCentreCanonical, isIntern });
    return rows;
  }, [filteredBaseRows, selectedCentreCanonical, isIntern, tY]);

  const completedPosting = useMemo(() => {
    const rows = filteredBaseRows.filter((row) => {
      const eY = row.endYMD;
      return Boolean(eY && eY < tY);
    });
    if (DEBUG_ROSTER) dbgGroup('computed completedPosting', { count: rows.length, tY, selectedCentreCanonical, isIntern });
    return rows;
  }, [filteredBaseRows, selectedCentreCanonical, isIntern, tY]);
  const load = useCallback(async () => {
    if (hasBadDates) {
      setErr('End date must be on or after Start date.');
      return;
    }

    setLoading(true);
    setErr('');
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      if (selectedCentreCanonical) qs.set('centre', selectedCentreCanonical);
      qs.set('limit', '5000');
      qs.set('cb', String(Date.now()));

      if (DEBUG_ROSTER) {
        dbgGroup('GET listRosters params', {
          from,
          to,
          centre: selectedCentreCanonical || '(All)',
          limit: 5000,
        });
      }

      // Direct call to roster endpoint preserved. toApiUrl() ensures localhost:3000 uses backend :5000.
      const res = await fetchJson(`/api/rosters?${qs.toString()}`);
      const payload = coerceList(res);

      if (DEBUG_ROSTER) {
        dbgGroup('GET listRosters response', {
          shape: Array.isArray(payload) ? 'array' : typeof payload,
          count: payload.length,
          sample: payload.slice(0, 3),
        });
      }

      setItems(payload);
    } catch (e) {
      const status = e?.response?.status || e?.status || null;
      const msg = e?.message || 'Failed to load roster';

      // Convert typical "no data" responses into empty list, not red banner.
      if (status === 400 || status === 404 || /visit not found|no roster|route not found/i.test(msg)) {
        if (DEBUG_ROSTER) dbgGroup('listRosters empty/tolerated error', { status, msg });
        setErr('');
        setItems([]);
      } else {
        setErr(msg);
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [from, to, selectedCentreCanonical, hasBadDates]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh when roster updated elsewhere or when tab refocuses.
  useEffect(() => {
    const onRefocus = () => load();
    const onRosterUpdated = () => load();
    window.addEventListener('focus', onRefocus);
    window.addEventListener('roster:updated', onRosterUpdated);
    return () => {
      window.removeEventListener('focus', onRefocus);
      window.removeEventListener('roster:updated', onRosterUpdated);
    };
  }, [load]);

  const summaryChips = (
    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
      <Chip size="small" label={`From: ${from || '—'}`} />
      <Chip size="small" label={`To: ${to || '—'}`} />
      <Chip size="small" label={`Centre: ${centre || '(All)'}`} />
      <Chip size="small" label={`Today: ${tY}`} />
      <Chip size="small" label={`Loaded: ${items?.length || 0}`} />
      <Chip size="small" label={`Visible: ${filteredBaseRows.length}`} />
      {DEBUG_ROSTER && (
        <Chip size="small" color="warning" label={`Canonical centre: ${selectedCentreCanonical || '(All)'}`} />
      )}
    </Stack>
  );

  const renderRosterCard = (row, prefix) => {
    const key = `${prefix}-${row._id}`;
    return (
      <Box key={key} className="p-3 border rounded-md dark:border-neutral-700">
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
          <Chip size="small" label={row.centreDisplay || '—'} variant="outlined" />
          {row.shift && <Chip size="small" label={row.shift} variant="outlined" />}
          {row.status && <Chip size="small" label={row.status} variant="outlined" />}
        </Stack>

        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {row.intern?.name || '—'}
        </Typography>

        <Typography variant="caption" sx={{ display: 'block', opacity: 0.78 }}>
          {dateRangeLabel(row.startYMD, row.endYMD)}
        </Typography>

        <Typography variant="caption" sx={{ display: 'block', opacity: 0.78 }}>
          {row.intern?.email || '—'}{row.intern?.mobile ? ` • ${row.intern.mobile}` : ''}
        </Typography>

        {(row.intern?.batchYear || row.intern?.batchName || row.intern?.rollNo) && (
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.78 }}>
            {[row.intern?.batchYear, row.intern?.batchName].filter(Boolean).join(' ')}
            {row.intern?.rollNo ? ` (${row.intern.rollNo})` : ''}
          </Typography>
        )}

        {row.notes && (
          <Tooltip title={row.notes} disableHoverListener={row.notes.length < 100}>
            <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.9 }}>
              {row.notes}
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box className="space-y-4 p-4 md:p-6">
      <Box className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Box>
          <Typography variant="h5" className="font-semibold">
            {isIntern ? 'My Roster / Postings' : 'Roster View'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.5 }}>
            View current and completed posting windows. Roster viewing remains separate from guided activity evidence and citizen OPD visit workflow.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {loading && (
            <Box className="flex items-center gap-2 opacity-80 text-sm">
              <CircularProgress size={16} /> Loading…
            </Box>
          )}
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading || hasBadDates}>
            Refresh
          </Button>
        </Stack>
      </Box>


      {summaryChips}

      {!!err && <Alert severity="error" onClose={() => setErr('')}>{err}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {!isIntern && (
            <>
              <TextField
                label="From"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
              <TextField
                label="To"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
                error={hasBadDates}
                helperText={hasBadDates ? 'End date must be on/after Start date' : ' '}
                disabled={loading}
              />
              <TextField
                label="Centre"
                select
                SelectProps={{ native: true }}
                value={centre}
                onChange={(event) => setCentre(event.target.value)}
                disabled={loading}
              >
                {CENTRES.map((c) => (
                  <option key={c || 'all'} value={c}>{c || '(All)'}</option>
                ))}
              </TextField>
              <TextField
                label="Search user / roll / email / mobile"
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder="e.g., name, batch, roll, mobile, email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

          {isIntern && (
            <TextField
              label="Search within my postings"
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="e.g., centre, shift, note"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}

          <Box className="flex items-center">
            <Button variant="outlined" onClick={load} disabled={loading || hasBadDates}>
              {loading ? 'Loading…' : 'Apply'}
            </Button>
          </Box>
        </div>
      </Paper>

      <Paper className="p-3 dark:bg-[#232326] dark:text-white" variant="outlined">
        <Box className="flex items-center justify-between mb-2">
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isIntern ? 'My posting (today)' : 'Current postings (today)'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.72 }}>
              {tY} • {postedToday.length} record{postedToday.length === 1 ? '' : 's'}
            </Typography>
          </Box>
          <Chip size="small" label={postedToday.length} variant="outlined" />
        </Box>
        <Divider className="mb-2" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {postedToday.length > 0 ? (
            postedToday.map((row) => renderRosterCard(row, 'today'))
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.65 }}>
              {isIntern ? 'You have no posting recorded for today.' : 'No current postings for today.'}
            </Typography>
          )}
        </div>
      </Paper>

      <Paper className="p-3 dark:bg-[#232326] dark:text-white" variant="outlined">
        <Box className="flex items-center justify-between mb-2">
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isIntern ? 'My current posting window' : 'Current posting windows'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.72 }}>
              Active posting windows as of {tY}
            </Typography>
          </Box>
          <Chip size="small" label={currentlyPosted.length} variant="outlined" />
        </Box>
        <Divider className="mb-2" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {currentlyPosted.length > 0 ? (
            currentlyPosted.map((row) => renderRosterCard(row, 'curr'))
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.65 }}>
              {isIntern ? 'You have no active posting in the selected window.' : 'No current postings in the selected window.'}
            </Typography>
          )}
        </div>
      </Paper>

      <Paper className="p-3 dark:bg-[#232326] dark:text-white" variant="outlined">
        <Box className="flex items-center justify-between mb-2">
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isIntern ? 'My completed postings' : 'Completed postings'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.72 }}>
              Completed before {tY}
            </Typography>
          </Box>
          <Chip size="small" label={completedPosting.length} variant="outlined" />
        </Box>
        <Divider className="mb-2" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {completedPosting.length > 0 ? (
            completedPosting.map((row) => renderRosterCard(row, 'done'))
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.65 }}>
              {isIntern ? 'No completed postings found in the selected window.' : 'No completed postings in the selected window.'}
            </Typography>
          )}
        </div>
      </Paper>
    </Box>
  );
}
